import React from 'react';
import Head from 'next/head';
import type {AppProps} from 'next/app';
import {useRouter} from 'next/router';
import {appWithTranslation} from 'next-i18next';
import configNextI18Next from 'next-i18next.config';
import {config} from '@modules/config';
import './global.css';

import AppRoot, {type Webview} from '@components/AppRoot';

const isMswEnabled =
	typeof window !== 'undefined' &&
	(process.env.NODE_ENV === 'development' ||
		process.env.NEXT_PUBLIC_ENABLE_MSW === 'true');

const mockingEnabledPromise = isMswEnabled
	? import('../src/mocks/browser').then(({worker}) =>
			worker.start({
				onUnhandledRequest: 'bypass',
			}),
		)
	: Promise.resolve();

const App: React.FC<AppProps> = ({Component, pageProps}) => {
	const {locale, locales, asPath} = useRouter();
	const [mockingReady, setMockingReady] = React.useState(!isMswEnabled);

	React.useEffect(() => {
		mockingEnabledPromise.then(() => setMockingReady(true));
	}, []);

	React.useEffect(() => {
		if (document.domain !== 'localhost') {
			try {
				// Used for some old browsers to enable some CORS stuff
				document.domain = config.get('host_cookie').slice(1);
			} catch {
				// noop
			}
		}
	}, []);

	if (!mockingReady) {
		return null;
	}

	return (
		<>
			<Head>
				<>
					<meta name="robots" content="noindex,nofollow" />
					<meta
						name="viewport"
						content="viewport-fit=cover, initial-scale=1.0, width=device-width, maximum-scale=1.0, user-scalable=no"
					/>
					{(locales || [])
						.filter((l) => l !== locale)
						.map((locale) => (
							<link
								key={locale}
								rel="alternate"
								hrefLang={locale}
								href={`/${locale}${asPath}`}
							/>
						))}
				</>
			</Head>
			<AppRoot webview={pageProps.webview as Webview}>
				<Component {...pageProps} />
			</AppRoot>
		</>
	);
};

export default appWithTranslation(App, configNextI18Next);
